import React, { useCallback, useEffect, useState } from 'react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { setSeoMetadata } from '../lib/seo';
import { getSlides } from '../tools/transfer/api';
import BackgroundSlideshow from '../tools/transfer/BackgroundSlideshow';
import TransferSend from '../tools/transfer/TransferSend';
import TransferSiteFooter from '../tools/transfer/TransferSiteFooter';
import TransferTopBar from '../tools/transfer/TransferTopBar';
import { useTransferTheme } from '../tools/transfer/theme';

/**
 * The File Transfer landing page, used as the site home page when the
 * "File Transfer as Home Page" flag is on: client slideshow edge to edge, the
 * menu across the top, a compact upload box on the left and the headline beside
 * it. The plain included page is TransferPage at /transfer.
 */
const TransferLanding = () => {
  const { siteName } = useSiteSettings();
  const [brand, setBrand] = useState(null);
  const [slides, setSlides] = useState([]);
  const [seconds, setSeconds] = useState(6);
  const [dark, toggleTheme] = useTransferTheme();
  const onConfig = useCallback((c) => setBrand(c.brand), []);

  useEffect(() => {
    getSlides().then((d) => { setSlides(d.slides); setSeconds(d.slide_seconds); }).catch(() => {});
  }, []);

  const headline = brand?.brand_headline || 'Send files. Move ideas.';
  const tagline = brand?.brand_tagline || 'Send big files, simply.';
  const description = `${tagline} Share large files with a private, expiring link.${siteName ? ` By ${siteName}.` : ''}`;

  useEffect(() => {
    setSeoMetadata({
      title: headline,
      description,
      keywords: 'send large files, file transfer, share files online, secure file sharing',
      canonicalPath: '/',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: brand?.brand_name || siteName || headline,
        description,
        applicationCategory: 'UtilitiesApplication',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      },
    });
  }, [brand, description, headline, siteName]);

  const ink = dark ? 'text-white' : 'text-slate-900';
  const soft = dark ? 'text-white/70' : 'text-slate-600';

  return (
    <div className={dark ? 'bg-slate-950' : 'bg-white'}>
      <div className="relative min-h-screen overflow-hidden flex flex-col" data-testid="transfer-landing">
        {slides.length ? (
          <BackgroundSlideshow slides={slides} seconds={seconds} dark={dark} />
        ) : (
          <div className={`absolute inset-0 ${dark ? 'bg-gradient-to-br from-slate-900 via-slate-950 to-indigo-950' : 'bg-gradient-to-br from-indigo-50 via-white to-orange-50'}`} />
        )}

        <TransferTopBar dark={dark} onToggleTheme={toggleTheme} />

        <main className="relative z-10 flex-1 flex items-center">
          <div className="w-full max-w-7xl mx-auto px-4 py-8 lg:py-12 grid lg:grid-cols-[22rem_minmax(0,1fr)] gap-8 lg:gap-24 items-center">
            <section className="order-2 lg:order-1 w-full max-w-sm mx-auto lg:mx-0" aria-label="Send files">
              <TransferSend skin={dark ? 'dark' : 'light'} onConfig={onConfig} />
            </section>
            <header className={`order-1 lg:order-2 ${ink}`}>
              {brand?.brand_name && <p className={`${soft} font-semibold uppercase tracking-widest text-xs mb-3`}>{brand.brand_name}</p>}
              <h1 className="text-5xl sm:text-6xl lg:text-8xl font-extrabold leading-[1.02] tracking-tight text-balance">{headline}</h1>
              <p className={`${soft} text-lg mt-4 max-w-md`}>{tagline}</p>
            </header>
          </div>
        </main>
      </div>
      <TransferSiteFooter dark={dark} text={brand?.footer_text} />
    </div>
  );
};

export default TransferLanding;
